import styled from '@emotion/styled'
import { css, keyframes } from '@emotion/react'

const FORCE_CLICK_HAND_DISPLAY_PX = 20
/** Inclina a mão para a esquerda — dedo aponta para o centro do alvo sem cobri-lo. */
const FORCE_CLICK_HAND_TILT_DEG = -26
/** Deslocamento fixo da mão em relação ao centro do alvo (todos os tamanhos). */
export const FORCE_CLICK_HAND_OFFSET_X_PX = 4
export const FORCE_CLICK_HAND_OFFSET_Y_PX = 7
/** Espaço extra no anchor para ripple + mão não serem cortados pelo box do alvo. */
export const FORCE_CLICK_AFFORDANCE_OVERFLOW_PX = 28
const FORCE_CLICK_RIPPLE_SIZE_PX = 22
const FORCE_CLICK_RIPPLE_DURATION_MS = 2400
const FORCE_CLICK_RIPPLE_MAX_SCALE = 2.05

export const FORCE_CLICK_RIPPLE_STAGGER_MS = 800

export const forceClickHandImageSizePx = FORCE_CLICK_HAND_DISPLAY_PX

const spotlightShieldPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 204, 0, 0.55);
    border-color: rgba(255, 204, 0, 1);
  }

  100% {
    box-shadow: 0 0 0 10px rgba(255, 204, 0, 0);
    border-color: rgba(255, 204, 0, 0.95);
  }
`

const FORCE_CLICK_RIPPLE_PRIMARY = 'rgba(212, 158, 0, 1)'
const FORCE_CLICK_RIPPLE_SECONDARY = 'rgba(0, 163, 168, 1)'

const forceClickBrandRipple = keyframes`
  0% {
    transform: scale(0.45);
    opacity: 0.95;
  }

  55% {
    opacity: 0.45;
  }

  100% {
    transform: scale(${FORCE_CLICK_RIPPLE_MAX_SCALE});
    opacity: 0;
  }
`

export const Overlay = styled.div<{ elevated?: boolean }>`
  position: fixed;
  inset: 0;
  z-index: ${({ elevated }) => (elevated ? 10050 : 9999)};
  pointer-events: auto;
`

export const OverlaySvg = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
`

export const SpotlightShield = styled.div<{
  elevated?: boolean
  $isPulsing?: boolean
  $isForceClick?: boolean
}>`
  position: fixed;
  z-index: ${({ elevated }) => (elevated ? 10052 : 10001)};
  border: 4px solid rgba(255, 204, 0, 0.95);
  border-radius: 12px;
  background: ${({ $isForceClick }) =>
    $isForceClick ? 'transparent' : 'rgba(255, 204, 0, 0.08)'};
  cursor: ${({ $isForceClick }) => ($isForceClick ? 'pointer' : 'default')};
  box-sizing: border-box;
  overflow: visible;
  animation: ${({ $isPulsing }) =>
    $isPulsing
      ? css`
          ${spotlightShieldPulse} 0.45s ease-out
        `
      : 'none'};
`

export const ForceClickHint = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: visible;
`

/** Ondas (raio) no centro do alvo — expandem para fora sem camada azul. */
export const ForceClickRippleStack = styled.div<{
  $centerX: number
  $centerY: number
}>`
  position: absolute;
  top: ${({ $centerY }) => $centerY}px;
  left: ${({ $centerX }) => $centerX}px;
  width: ${FORCE_CLICK_RIPPLE_SIZE_PX}px;
  height: ${FORCE_CLICK_RIPPLE_SIZE_PX}px;
  transform: translate(-50%, -50%);
  z-index: 2;
`

export const ForceClickRippleRing = styled.span<{
  $variant: 'primary' | 'secondary'
  $delayMs: number
}>`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid
    ${({ $variant }) =>
      $variant === 'primary'
        ? FORCE_CLICK_RIPPLE_PRIMARY
        : FORCE_CLICK_RIPPLE_SECONDARY};
  transform-origin: 50% 50%;
  animation: ${forceClickBrandRipple} ${FORCE_CLICK_RIPPLE_DURATION_MS}ms
    cubic-bezier(0.22, 0.61, 0.36, 1) infinite;
  animation-delay: ${({ $delayMs }) => `${$delayMs}ms`};
`

/** Mão no mesmo offset do centro do alvo que o ripple (compacto ou card grande). */
export const ForceClickHandWrap = styled.div<{
  $centerX: number
  $centerY: number
}>`
  position: absolute;
  top: ${({ $centerY }) => $centerY}px;
  left: ${({ $centerX }) => $centerX}px;
  z-index: 3;
  width: ${FORCE_CLICK_HAND_DISPLAY_PX}px;
  height: ${FORCE_CLICK_HAND_DISPLAY_PX}px;
  pointer-events: none;
  transform: translate(
      ${FORCE_CLICK_HAND_OFFSET_X_PX}px,
      ${FORCE_CLICK_HAND_OFFSET_Y_PX}px
    )
    rotate(${FORCE_CLICK_HAND_TILT_DEG}deg);
`

export const ForceClickHandImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  pointer-events: none;
  user-select: none;
`

/** Posiciona raio + mão no alvo do `forceClick` (sem overlay azul). */
export const ForceClickAffordanceAnchor = styled.div<{ elevated?: boolean }>`
  position: fixed;
  z-index: ${({ elevated }) => (elevated ? 10054 : 10053)};
  pointer-events: none;
  overflow: visible;
`
